import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Mail, Phone, MapPin, PhoneCall, Siren, HeartHandshake, Baby, Globe, Clock } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const GROUPS = [
  {
    icon: Siren,
    title: "Immediate Danger",
    note: null,
    items: [
      ["Emergency Services", "911", "If you or someone else is in immediate danger, call 911 or go to your nearest emergency department.", "tel:911", "24/7"],
    ],
  },
  {
    icon: PhoneCall,
    title: "Halton Region — Milton, Burlington, Oakville",
    note: null,
    items: [
      ["COAST Halton — Crisis Outreach and Support Team", "1-877-825-9011", "24/7 mental health crisis support line for Halton Region. Mobile crisis team and telephone support for people experiencing a mental health crisis.", "tel:+18778259011", "24/7"],
      ["Halton Women's Place", "905-878-8555", "24-hour crisis line for women in Halton experiencing domestic violence or abuse. Shelter, safety planning, and support.", "tel:+19058788555", "24/7"],
      ["211 Ontario — Halton Community Services", "Dial 2-1-1", "Free, confidential navigation to local community, social, and mental health services across Halton and Ontario.", "tel:211", "24/7"],
    ],
  },
  {
    icon: HeartHandshake,
    title: "Ontario-Wide Mental Health Lines",
    note: null,
    items: [
      ["9-8-8 Suicide Crisis Helpline", "Call or text 9-8-8", "Free, confidential, 24/7 suicide prevention and mental health crisis support, anywhere in Canada.", "tel:988", "24/7 · call or text"],
      ["ConnexOntario", "1-866-531-2600", "Ontario's mental health, addiction, and problem gambling helpline — connects you to services and treatment information.", "tel:+18665312600", "24/7"],
      ["Telehealth Ontario", "Dial 8-1-1", "Free health advice from a registered nurse, including mental health concerns and where to find care.", "tel:811", "24/7"],
      ["Assaulted Women's Helpline", "1-866-863-0511", "Province-wide, 24/7 crisis line for women experiencing abuse. TTY: 1-866-863-7868.", "tel:+18668630511", "24/7"],
    ],
  },
  {
    icon: Baby,
    title: "Children & Youth",
    note: null,
    items: [
      ["Kids Help Phone", "1-800-668-6868", "24/7 professional counselling and crisis support for young people. Text CONNECT to 686868 to reach a trained responder.", "tel:+18006686868", "24/7 · call or text"],
    ],
  },
  {
    icon: Globe,
    title: "Culturally Specific Support",
    note: null,
    items: [
      ["Naseeha Muslim Helpline", "1-866-627-3342", "Confidential helpline for Muslims in North America — mental health support grounded in cultural and faith context.", "tel:+18666273342", "7 days a week"],
    ],
  },
];

export default function CrisisSupport() {
  useEffect(() => {
    document.title = "Crisis Support & Mental Health Hotlines — Halton Region & Ontario | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Mental health crisis lines for Milton, Burlington, Halton Region and Ontario — 9-8-8, COAST Halton, Kids Help Phone, ConnexOntario, and more. In immediate danger, call 911.");
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-4xl">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/resources" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> All resources
        </motion.a>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Crisis Support · Halton Region &amp; Ontario</motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="crisis-heading"
          className="font-serif-display mt-4 text-[clamp(2.2rem,5vw,4rem)] leading-[1.08] font-normal">
          Help is available <em className="text-[#6e4550]">right now.</em>
        </motion.h1>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
          <p>
            Haya Healing Psychotherapy is not a crisis service — sessions are by appointment. If you need support
            right now, these are the lines to call. Every service listed here is free and confidential.
          </p>
        </motion.div>
      </section>
      {GROUPS.map((g, gi) => (
        <section key={g.title} data-testid={`crisis-group-${gi + 1}`} className={`px-6 md:px-14 py-14 md:py-16 border-b border-[#2c1f20]/10 ${gi % 2 ? "bg-[#f1ece4]/50" : ""}`}>
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#f4e6e2] flex items-center justify-center text-[#8a5a63]"><g.icon size={18} /></span>
              <h2 className="font-serif-display text-2xl md:text-3xl">{g.title}</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5 mt-8 max-w-6xl">
            {g.items.map(([name, number, desc, href, hours], i) => (
              <Reveal key={name} i={i % 4} className="card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif-display text-xl leading-snug">{name}</p>
                    <a href={href} data-testid={`hotline-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 28)}`}
                      className="inline-block mt-2 text-lg font-semibold text-[#6e4550] hover:underline underline-offset-4">{number}</a>
                  </div>
                  <span className="chip flex items-center gap-1.5 shrink-0"><Clock size={12} /> {hours}</span>
                </div>
                <p className="text-sm text-[#2c1f20]/70 mt-3 leading-relaxed">{desc}</p>
              </Reveal>
            ))}
          </div>
        </section>
      ))}
      <section className="px-6 md:px-14 py-10">
        <p className="text-xs text-[#2c1f20]/50 leading-relaxed max-w-5xl">
          Crisis lines for Milton, Burlington, Oakville, Halton Region, and Ontario. This page is provided as a community resource by Haya Healing Psychotherapy — we are not affiliated with the services listed and cannot guarantee their availability. Numbers verified September 2026. Care led by Momna Sajid, Registered Psychotherapist (Qualifying), CRPO.
        </p>
      </section>
      <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">When you're ready</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">Ongoing support starts with a <em className="text-[#6e4550]">complimentary consultation.</em></h2></Reveal>
        <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
          <a data-testid="crisis-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
        </Reveal>
        <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
      </footer>
    </div>
  );
}
